import React, { useState, useEffect, useContext } from 'react';
import { 
  Box, Heading, VStack, Text, Button, Flex, 
  useColorModeValue, Badge, Icon, Avatar, HStack 
} from '@chakra-ui/react';
import { MdVideoCameraFront, MdGroups, MdOutlineTimer } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../services/axiosInstance';
import AuthContext from '../services/AuthContext';

export default function ActiveMeetings() {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const bgColor = useColorModeValue("white", "gray.800");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const borderColor = useColorModeValue("gray.100", "gray.700");
  
  // Define these here to avoid calling hooks inside callbacks or after early returns
  const headingColor = useColorModeValue("gray.800", "white");
  const itemBorderColor = useColorModeValue("gray.200", "gray.600");
  const itemBgColor = useColorModeValue("white", "gray.800");

  const fetchMeetings = async () => {
    try {
      const response = await axiosInstance.get(`/meetings/active`);
      if (response.data.success) {
        setMeetings(response.data.data);
      }
    } catch (err) {
      console.error("Failed to fetch active meetings", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMeetings();
    const interval = setInterval(fetchMeetings, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleJoin = (roomName) => {
    navigate(`/meet/${roomName}`);
  };

  const handleEnd = async (roomName) => {
    try {
      await axiosInstance.post(`/meetings/end/${roomName}`);
      fetchMeetings();
    } catch (err) {
      console.error("Failed to end meeting", err);
    }
  };

  if (loading && meetings.length === 0) return null;

  if (meetings.length === 0) {
    return (
      <Flex 
        direction="column" 
        align="center" 
        justify="center" 
        p={10} 
        bg={bgColor} 
        borderRadius="2xl" 
        borderWidth="1px"
        borderColor={borderColor}
        borderStyle="dashed"
      >
        <Box p={4} bg="gray.50" rounded="full" color="gray.400" mb={4}>
          <Icon as={MdOutlineTimer} w={10} h={10} />
        </Box>
        <Heading size="md" color="gray.500" mb={2} dir="rtl">لا توجد حصص نشطة حالياً</Heading>
        <Text color="gray.400" dir="rtl">الرجاء الانتظار حتى يقوم المدرس ببدء الحصة</Text>
      </Flex>
    );
  }

  return (
    <Box p={8} shadow="xl" borderWidth="1px" borderColor={borderColor} borderRadius="2xl" bg={bgColor} width="100%">
      <Flex justify="space-between" align="center" mb={6} dir="rtl">
        <Flex align="center" gap={3}>
          <Box position="relative">
            <Icon as={MdVideoCameraFront} w={8} h={8} color="red.500" />
            <Box position="absolute" top={0} right={0} w={3} h={3} bg="red.500" rounded="full" className="pulse-animation" />
          </Box>
          <Heading size="lg" color={headingColor}>
            الحصص الجارية الآن
          </Heading>
        </Flex>
        <Badge colorScheme="red" p={2} borderRadius="lg" fontSize="sm">
          {meetings.length} مباشر
        </Badge>
      </Flex>

      <VStack spacing={4} align="stretch" dir="rtl">
        {meetings.map((meeting) => (
          <Flex 
            key={meeting._id} 
            p={5} 
            borderWidth="1px" 
            borderColor={itemBorderColor}
            borderRadius="xl" 
            justify="space-between" 
            align={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={{ base: 4, md: 0 }}
            bg={itemBgColor}
            transition="all 0.3s"
            _hover={{ 
              bg: hoverBg, 
              transform: "translateY(-3px)", 
              boxShadow: "lg",
              borderColor: "blue.300"
            }}
          >
            <HStack spacing={4}>
              <Avatar size="md" name={meeting.createdBy?.username} bg="blue.500" color="white" />
              <Box>
                <Flex align="center" gap={2} mb={1}>
                  <Text fontWeight="bold" fontSize="lg" color={headingColor}>
                    {meeting.roomName}
                  </Text>
                  <Badge colorScheme="green" variant="subtle" fontSize="xs" px={2} py={0.5} rounded="full">
                    نشط
                  </Badge>
                  <Badge colorScheme="blue" variant="subtle" fontSize="xs" px={2} py={0.5} rounded="full">
                    ID: {meeting.meetingId}
                  </Badge>
                </Flex>
                <HStack color="gray.500" fontSize="sm" spacing={4}>
                  <Flex align="center" gap={1}>
                    <Icon as={MdGroups} />
                    <Text>الأستاذ: {meeting.createdBy?.username || 'غير معروف'}</Text>
                  </Flex>
                  <Text>• تم البدء منذ قليل</Text>
                </HStack>
              </Box>
            </HStack>
            
            <HStack w={{ base: "100%", md: "auto" }}>
              {(user?.role === 'admin' || user?._id === meeting.createdBy?._id) && (
                <Button 
                  colorScheme="red" 
                  variant="outline"
                  size="md" 
                  onClick={() => handleEnd(meeting.roomName)}
                  borderRadius="lg"
                  flex={{ base: 1, md: "auto" }}
                >
                  إنهاء
                </Button>
              )}
              <Button 
                colorScheme="blue" 
                size="md" 
                onClick={() => handleJoin(meeting.meetingId)}
                borderRadius="lg"
                px={8}
                flex={{ base: 1, md: "auto" }}
                boxShadow="0 4px 14px 0 rgba(49, 130, 206, 0.39)"
                _hover={{ transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(49, 130, 206, 0.23)" }}
              >
                انضمام الآن
              </Button>
            </HStack>
          </Flex>
        ))}
      </VStack>
      
      <style>
        {`
          @keyframes pulse {
            0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(229, 62, 62, 0.7); }
            70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(229, 62, 62, 0); }
            100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(229, 62, 62, 0); }
          }
          .pulse-animation {
            animation: pulse 2s infinite;
          }
        `}
      </style>
    </Box>
  );
}
